// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { advisorApi } from '../api/advisorApi';
import { useBranding } from './BrandingContext';
import type { AdvisorProfile } from '../types/advisor';

interface AdvisorContextValue {
  advisor: AdvisorProfile | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Whether the current user is a support staff member */
  isSupport: boolean;
  /** Whether the current user is an admin */
  isAdmin: boolean;
}

const AdvisorContext = createContext<AdvisorContextValue | null>(null);

type AdvisorProviderProps = Readonly<{
  children: ReactNode;
}>;

/**
 * Extract brokerageId from the current hostname subdomain
 * Examples:
 *  - tb3.advisor.localhost -> tb3
 *  - acme.advisor.example.com -> acme
 *  - localhost:3002 -> tb1 (fallback for local dev)
 */
const extractBrokerageIdFromHostname = (): string => {
  const hostname = globalThis.location.hostname;

  // Handle localhost for local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'tb1'; // Default fallback
  }

  // Extract subdomain (first part before first dot)
  // Example: tb3.advisor.localhost -> tb3
  const parts = hostname.split('.');
  if (parts.length > 0) {
    return parts[0];
  }

  return 'tb1'; // Fallback
};

/**
 * AdvisorProvider - Manages advisor profile state for the authenticated session
 *
 * This provider:
 * 1. Loads the advisor profile once on mount
 * 2. Sets up the branding context with the advisor's brokerage
 * 3. Provides the profile to all child components
 * 4. Offers a refetch() method for manual refresh (e.g., after photo upload)
 */
export function AdvisorProvider({ children }: AdvisorProviderProps) {
  const [advisor, setAdvisor] = useState<AdvisorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setBrokerageId } = useBranding();

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Set brokerageId in API client before making requests
      const brokerageId = extractBrokerageIdFromHostname();
      advisorApi.setBrokerageId(brokerageId);

      const data = await advisorApi.getProfile();
      setAdvisor(data);

      // Load branding for the advisor's brokerage
      if (data.brokerageId) {
        setBrokerageId(data.brokerageId.toString());
      }
    } catch (err) {
      console.error('Failed to load advisor profile:', err);
      // Don't set error if redirecting to login (401 handled by axios interceptor)
      if (err instanceof Error && (err.message?.includes('login') || err.message?.includes('network'))) {
        return;
      }
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
        : 'Failed to load profile';
      setError(errorMessage || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [setBrokerageId]);

  const refetch = useCallback(async () => {
    await loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const value = useMemo<AdvisorContextValue>(() => ({
    advisor,
    loading,
    error,
    refetch,
    isSupport: advisor?.role === 'SUPPORT',
    isAdmin: advisor?.role === 'ADMIN',
  }), [advisor, loading, error, refetch]);

  return (
    <AdvisorContext.Provider value={value}>
      {children}
    </AdvisorContext.Provider>
  );
}

/**
 * Hook to access advisor context
 */
export function useAdvisor(): AdvisorContextValue {
  const context = useContext(AdvisorContext);

  if (!context) {
    throw new Error('useAdvisor must be used within an AdvisorProvider');
  }

  return context;
}
